import React, { useMemo, useState } from 'react';
import BreakDownHeader from '@/pages/dashboard/components/BreakDownHeader';
import { getLocale, useIntl } from 'umi';
import { CountriesContextTabProps } from '../_type';
import './index.scss';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { getName } from '@home/utils/common';
import classNames from 'classnames';

const CountriesContextTab = (props: CountriesContextTabProps) => {
  const { className, style, children, data, ...resetProps } = props || {};

  // 从 resetProps 中获取 config（如果存在）
  const { config } = (resetProps as any) || {};

  const {
    regionDataType: regionDataTypeInit,
    tabOrder = ['os', 'country', 'region'],
    optionsInit: propsOptionsInit = [],
    defaultRegionDataType = '',
    tabMsg
  } = data || { regionDataType: 'os' };
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEN = getLocale() === 'en-US';
  const [tabsVal, setTabsVal] = useState(regionDataTypeInit);

  const options = useMemo(() => {

    // 扩展自定义tab选项
    const extOption = propsOptionsInit?.map((item: { textEn: any; textCn: any; value?: string; }) => ({
      ...item,
      text: isEN ? item?.textEn : item?.textCn
    })) || [];

    // 获取 tab 文本的辅助函数（优先使用 tabMsg 配置）
    const getTabText = (value: string, defaultText: string) => {
      const tabConfig = tabMsg?.[value];
      if (tabConfig) {
        // 优先使用 Name，如果没有则后回退到默认文本
        return getName(isEN, (tabConfig?.nameEn || defaultText), (tabConfig?.nameCn || defaultText));
      }
      return defaultText;
    };

    const optionsInit = [
      { text: getTabText('os', formatMessage({ id: 'common_System' })), value: 'os' },
      { text: getTabText('country', formatMessage({ id: 'common_Country' })), value: 'country' },
      { text: getTabText('region', formatMessage({ id: 'common_Region' })), value: 'region' },
      { text: getTabText('channel', formatMessage({ id: 'common_Channel' })), value: 'channel' },
      { text: getTabText('zone', formatMessage({ id: 'common_Zone' })), value: 'zone' },
      { text: getTabText('language', formatMessage({ id: 'common_Language' })), value: 'language' },
      // 收入页面
      {
        text: getTabText('mtx_revenue', formatMessage(
          { id: 'pc_monetization_MTX_Revenue' },
          { unit: getMonetUnit() },
        )),
        value: 'mtx_revenue',
      },
      {
        text: getTabText('platform_mtx_revenue', formatMessage(
          { id: 'pc_monetization_MTX_Revenue_dl2' },
          { unit: getMonetUnit() },
        )),
        value: 'platform_mtx_revenue',
      },
      {
        text: getTabText('mtx_arpu', formatMessage(
          { id: 'pc_monetization_MTX_ARPU' },
          { unit: getMonetUnit() },
        )),
        value: 'mtx_arpu',
      },
      {
        text: getTabText('mtx_arppu', formatMessage(
          { id: 'pc_monetization_MTX_ARPPU' },
          { unit: getMonetUnit() },
        )),
        value: 'mtx_arppu',
      },
      {
        text: getTabText('platform_mtx_arpu', formatMessage(
          { id: 'pc_monetization_MTX_ARPU_dl2' },
          { unit: getMonetUnit() },
        )),
        value: 'platform_mtx_arpu',
      },
      {
        text: getTabText('platform_mtx_arppu', formatMessage(
          { id: 'pc_monetization_MTX_ARPPU_dl2' },
          { unit: getMonetUnit() },
        )),
        value: 'platform_mtx_arppu',
      },
      {
        text: getTabText('mtx_payers', formatMessage({ id: props.isDl2 ? 'pc_monetization_mtx_payer_dl2' : 'pc_overview_MTX_Payer' })),
        value: 'mtx_payers',
      },
      {
        text: getTabText('mtx_payer_rate', formatMessage({ id: props.isDl2 ? 'pc_monetization_mtx_payer_rate_dl2' : 'pc_overview_MTX_Payer_Rate' })),
        value: 'mtx_payer_rate',
      },
      ...(extOption || [])
    ];

    const optionsSorted = [];

    // eslint-disable-next-line semi-spacing
    for (let i = 0; i < tabOrder.length; i++) {
      const order = tabOrder[i];
      const option = optionsInit.find((option) => option.value === order);
      if (option) {
        optionsSorted.push(option);
      }
    }
    return optionsSorted;
  }, [isEN, tabOrder, propsOptionsInit, tabMsg, formatMessage, props.isDl2]);

  return (
    <div
      className={classNames("dbd-countries-context-box", className)}
      style={style}
    >
      <BreakDownHeader
        options={options}
        style={(resetProps as any)?.style}
        defaultValue={regionDataTypeInit}
        // 指标tab切换类型不需要segment-light-china类名的样式设置
        isCnClass={!defaultRegionDataType}
        onChange={(val: string) => {
          setTabsVal(val);
        }}
      />
      {
        // 保证是jsx数组，而不是其他类型数组
        React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...resetProps,
            data: { ...data, regionDataType: tabsVal, tabMsg },
          })
        )
      }
    </div>
  );
};

export default CountriesContextTab;
